/**
 * Email translations: the network's own wording for the emails the API sends
 * — password resets, line item and creative notifications, reports and so on.
 * Each translation covers one email type and can be narrowed to a locale, a
 * user interface and a set of applications. Any part left empty (subject,
 * heading, body, footer) falls back to the API's own English default, which
 * `emailtranslationdefaults` returns per email type.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { getContext } from '@/services/networks/networksApi'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'
import { mapRef, type EmailTemplateRef } from '@/services/networks/emailTemplatesApi'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** The `NotificationMessage` values a translation can be made for, as the old UI offers them. */
const BASE_EMAIL_TYPES = [
  'PASSWORD_RESET_ACCOUNT_LOCKED',
  'PASSWORD_RESET_FORGOT_PASSWORD',
  'PASSWORD_RESET_NEW_USER',
  'PASSWORD_RESET_SIGNUP_USER',
  'PASSWORD_RESET_SIGNUP_USER_EXISTS',
  'REPORT_COMPLETE',
  'LINE_ITEM_DELETED',
  'LINE_ITEM_RUNNING',
  'LINE_ITEM_SUBMITTED',
  'LINE_ITEM_REJECTED',
  'LINE_ITEM_STOPPED_RUNNING',
  'LINE_ITEM_ENDED_END_DATE_PASSED',
  'LINE_ITEM_ENDED_OBJECTIVE_REACHED',
  'CREATIVE_REJECTED',
  'API_KEY_EXPIRING',
  'API_KEY_EXPIRED',
]

/** Invitations for an advertiser to update its details, sent on marketplace and political networks. */
const ADVERTISER_INVITE_EMAIL_TYPES = [
  'ADVERTISER_UPDATE_INVITE',
  'ADVERTISER_UPDATE_INVITE_REMINDER',
]

/** The email types the network can translate. */
export function emailTranslationTypes(
  network: {
    marketplacePlatform?: boolean
    politicalApp?: boolean
  } | null
): string[] {
  return network?.marketplacePlatform || network?.politicalApp
    ? [...BASE_EMAIL_TYPES, ...ADVERTISER_INVITE_EMAIL_TYPES]
    : BASE_EMAIL_TYPES
}

/** The parts of an email a translation can replace. */
export const EMAIL_CONTENT_FIELDS = [
  'emailSubject',
  'emailHeading',
  'emailBody',
  'emailFooter',
] as const
export type EmailContentField = (typeof EMAIL_CONTENT_FIELDS)[number]

/** The API's own wording for an email type, and the variables it can use. */
export interface EmailTranslationDefault extends Record<EmailContentField, string> {
  messageDefinition: string
  parameters: string[]
}

/** A user interface the network's users can sign in to, which a translation can be limited to. */
export interface EmailUserInterface {
  code: string
  url: string
}

export interface EmailTranslation extends Record<EmailContentField, string> {
  id: string
  name: string
  description: string
  messageDefinition: string
  replyTo: string
  /** Empty applies to every application. */
  applications: string[]
  /** Empty applies to every locale. */
  locale: string
  /** A user interface code; empty applies to every interface. */
  userInterface: string
  /** Empty uses the network's default email template. */
  emailTemplate: EmailTemplateRef | null
  team: EmailTemplateRef | null
  objectState: string
  createTime: string
  updateTime: string
  validationWarnings: ValidationWarning[]
}

export interface EmailTranslationInput extends Partial<Record<EmailContentField, string>> {
  name: string
  description?: string
  messageDefinition?: string
  replyTo?: string
  applications?: string[]
  locale?: string
  userInterface?: string
  /** An email template id; empty uses the default template. */
  emailTemplate?: string
  /** `null` takes the translation off its team. */
  team?: { id: string } | null
  objectState?: string
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')

function mapEmailTranslation(r: Record<string, unknown>): EmailTranslation {
  return {
    id: str(r.id),
    name: str(r.name),
    description: str(r.description),
    messageDefinition: str(r.messageDefinition),
    emailSubject: str(r.emailSubject),
    emailHeading: str(r.emailHeading),
    emailBody: str(r.emailBody),
    emailFooter: str(r.emailFooter),
    replyTo: str(r.replyTo),
    applications: Array.isArray(r.applications) ? (r.applications as string[]) : [],
    locale: str(r.locale),
    userInterface: str(r.userInterface),
    emailTemplate: mapRef(r.emailTemplate),
    team: mapRef(r.team),
    objectState: str(r.objectState),
    createTime: str(r.createTime),
    updateTime: str(r.updateTime),
    validationWarnings: mapValidationWarnings(r.validationWarnings),
  }
}

function mapDefault(r: Record<string, unknown>): EmailTranslationDefault {
  return {
    messageDefinition: str(r.messageDefinition),
    // The defaults come from template files, so carry stray whitespace.
    emailSubject: str(r.emailSubject).trim(),
    emailHeading: str(r.emailHeading).trim(),
    emailBody: str(r.emailBody).trim(),
    emailFooter: str(r.emailFooter).trim(),
    parameters: Array.isArray(r.parameters) ? (r.parameters as string[]) : [],
  }
}

export interface EmailTranslationListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

/** One page of email translations, narrowed by the given filters on the server. */
async function fetchEmailTranslationPage(
  { search, status, sort }: EmailTranslationListOptions,
  page: number,
  pageSize: number
): Promise<ListPage<EmailTranslation>> {
  const params = new URLSearchParams({
    ...searchParams('emailTranslations', search),
    context: getContext(),
    includeHidden: 'false',
    ...statusParams(status),
    ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
    page: String(page),
    pageSize: String(pageSize),
    // On a marketplace, limits a team admin to their own teams' translations.
    showEditableOnly: 'true',
  })
  const res = await apiFetch(`${API_BASE}/emailtranslations?${params}`, { headers: JSON_HEADERS })
  if (!res.ok) await throwApiError(res, 'Failed to fetch email translations')
  const data = (await res.json()) as { totalCount?: number }
  const results = toArray<Record<string, unknown>>(data).map(mapEmailTranslation)
  return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
}

export const EmailTranslationsApi = {
  getPage: ({ page = 1, pageSize = 25, ...filters }: EmailTranslationListOptions = {}) =>
    fetchEmailTranslationPage(filters, page, pageSize),

  getById: async (id: string): Promise<EmailTranslation> => {
    const res = await apiFetch(`${API_BASE}/emailtranslations/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch email translation')
    return mapEmailTranslation(await res.json())
  },

  create: async (id: string, input: EmailTranslationInput): Promise<EmailTranslation> => {
    const res = await apiFetch(`${API_BASE}/emailtranslations/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...input }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create email translation')
    return mapEmailTranslation(await res.json())
  },

  update: async (id: string, input: EmailTranslationInput): Promise<EmailTranslation> => {
    const res = await apiFetch(`${API_BASE}/emailtranslations/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save email translation')
    return mapEmailTranslation(await res.json())
  },

  /**
   * Sends the saved translation, filled with dummy data. Only a masquerading
   * support user may name the recipient; anyone else gets it themselves.
   */
  sendTest: async (id: string, to?: string): Promise<void> => {
    const params = new URLSearchParams({ context: getContext(), ...(to ? { to } : {}) })
    const res = await apiFetch(`${API_BASE}/emailtranslations/sendtest/${id}?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to send test email')
  },

  /** The API's own wording for each email type. Not network specific. */
  getDefaults: async (): Promise<EmailTranslationDefault[]> => {
    const res = await apiFetch(`${API_BASE}/emailtranslationdefaults`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch default email translations')
    return toArray<Record<string, unknown>>(await res.json()).map(mapDefault)
  },

  getUserInterfaces: async (): Promise<EmailUserInterface[]> => {
    const res = await apiFetch(
      `${API_BASE}/emailtranslationdefaults/userInterfaces?context=${getContext()}`,
      { headers: JSON_HEADERS }
    )
    if (!res.ok) await throwApiError(res, 'Failed to fetch user interfaces')
    return toArray<Record<string, unknown>>(await res.json()).map((r) => ({
      code: str(r.code),
      url: str(r.url),
    }))
  },
}
